import { formDataPostRequest, getRequest, postRequest, putRequest } from 'config/Axios/baseConfig';
import {
  AddJobNoteRequest,
  bulkUploadDataValidationInterface,
  BulkUploadDataValidationInterfaceInstanceRequest,
  bulkUploadPpsResponseInterface,
  EditJobsRequestSchema,
  IGetJobs,
  IGetJobsResponseSchema,
  ISaveSingleJobRequestSchema,
  JobNotesResponse,
  SaveJobResponseSchema,
  SharedFilterResponse,
  SingleJobNoteResponse,
  StatusTransition,
  UpdateJobNoteRequest,
  UpdateSharedFilterRequest,
  UpdateSharedFilterResponse
} from 'Interfaces/JobsInterface';
import { SysproJobsResponse } from 'screens/JobPaste/types';

// Interface for Redis edit check response
export interface RedisEditCheckResponse {
  check: boolean;
  updated_data?: {
    status: string;
    data: {
      roomId: string;
      roomName: string;
      jobs: Array<{
        id: string | null;
        jobId: string;
        serial: number;
        priority: string | number; // PRIORITY_TYPE: '1' | '3' or number
        status: string; // JOB_TYPES: 'released' | 'ready' | 'inProcess' | 'staged' | 'prestaged' | 'in process' | 'pre-staged'
        room: string;
        ndc: string;
        description: string;
        qtyOfContainers: number;
        qtyPerContainer: number;
        type: string; // RUN_TYPE
        qtyUnitsToPackage: string;
        confirmedDate: string;
        notes: Array<{
          id: string;
          createdBy: string;
          noteText: string;
          created_at: string;
          updated_at: string;
          isEdited: boolean;
        }>;
        cleanProjected: string | null;
        bpmProjected: number | null;
        cleanOverride: string | null;
        bpmOverride: number | null;
        numberOfBottlesInAtShiftStart: number | null;
        jobStartTime: string;
        dirty_fields?: Array<{ field: string; timestamp: number }>;
      }>;
      timestamp: number;
    };
    active_users: string[];
  };
  message: string;
  job_count?: number;
  active_users?: string[];
  error?: string;
}

// Interface for CRDT process edit response
export interface CRDTProcessEditResponse {
  status: string;
  message: string;
  room_id: string;
  merged_data?: {
    jobs: Record<string, any>; // CRDT format jobs
  };
  background_task_id?: string;
  task_warning?: string;
  error?: string;
}

// Interface for completed jobs response
export interface CompletedJobsResponse {
  jobs: CompletedJob[];
  message: string;
  success: boolean;
  total_results: number;
  search_term: string;
  limit: number;
}

// Interface for fetch all rooms from Redis response
export interface FetchAllRoomsFromRedisResponse {
  rooms: Array<{
    id: string;
    name: string;
    color: string;
    shortCode: string;
    isExpanded: boolean;
    updatedAt: string | null;
    RoomTimeTotal: {
      days: number;
      hours: number;
      minutes: number;
    };
    jobs: Array<{
      id: string | null;
      jobId: string;
      serial: number;
      priority: string | number;
      status: string;
      room: string;
      ndc: string;
      description: string;
      qtyOfContainers: number;
      qtyPerContainer: number;
      type: string;
      qtyUnitsToPackage: string;
      confirmedDate: string;
      notes: Array<{
        id: string;
        createdBy: string;
        noteText: string;
        created_at: string;
        updated_at: string;
        isEdited: boolean;
      }>;
      cleanProjected: string | null;
      bpmProjected: number | null;
      cleanOverride: string | null;
      bpmOverride: number | null;
      numberOfBottlesInAtShiftStart: number | null;
      jobStartTime: string;
      dirty_fields?: Array<{ field: string; timestamp: number }>;
    }>;
  }>;
  message: string;
  success: boolean;
  total_rooms: number;
}

export interface CompletedJob {
  id: string;
  jobId: string; // Note: API uses jobId, not job_id
  serial: number;
  priority: number;
  status: string;
  room: string;
  ndc: string;
  description: string;
  qtyOfContainers: number; // Note: API uses qtyOfContainers, not qty_of_containers_to_fill
  qtyPerContainer: number; // Note: API uses qtyPerContainer, not qty_per_container
  type: string; // Note: API uses type, not run_type
  qtyUnitsToPackage: string; // Note: API uses qtyUnitsToPackage, not qty_units_to_package
  confirmedDate: string; // Note: API uses confirmedDate, not confirmed_date
  notes: Array<{
    id: string;
    createdBy: string;
    noteText: string;
    created_at: string;
    updated_at: string;
    isEdited: boolean;
  }>;
  cleanProjected: string | null;
  bpmProjected: number | null;
  cleanOverride: string | null;
  bpmOverride: number | null;
  numberOfBottlesInAtShiftStart: number | null; // Note: API uses numberOfBottlesInAtShiftStart, not number_of_bottles_at_shift_start
  jobStartTime: string | null; // Note: API uses jobStartTime, not job_start_time
}

// Interface for flush all drafts response
export interface FlushAllDraftsResponse {
  status: string;
  message: string;
  keys_deleted: string[];
  summary: {
    room_drafts: number;
    room_users: number;
    room_calc_locks: number;
    total_deleted: number;
  };
}

// Interface for flush all drafts error response
export interface FlushAllDraftsError {
  error: string;
  status: string;
  message?: string;
}

export const JobPasteServices = {
  saveJob: (payload: ISaveSingleJobRequestSchema): Promise<SaveJobResponseSchema> => {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await postRequest<SaveJobResponseSchema>('pps/save-single-job/', payload);
        resolve(data);
      } catch (error) {
        reject(error);
      }
    });
  },

  fetch_jobs: (payload: IGetJobs): Promise<IGetJobsResponseSchema> => {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await postRequest<IGetJobsResponseSchema>('pps/fetch-jobs/', payload);
        resolve(data);
      } catch (error) {
        reject(error);
      }
    });
  },

  fetchSysproJobs: (): Promise<SysproJobsResponse> => {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await getRequest<SysproJobsResponse>('pps/syspro/jobs/');
        resolve(data);
      } catch (error) {
        reject(error);
      }
    });
  },

  fetchCompletedJobs: (searchTerm: string = ''): Promise<CompletedJobsResponse> => {
    return new Promise(async (resolve, reject) => {
      try {
        // Build URL with parameters
        const params = new URLSearchParams();

        // Add search parameter only if provided and not empty
        if (searchTerm && searchTerm.trim()) {
          params.append('search', searchTerm.trim());
        }

        // Always limit to 20 results
        params.append('limit', '20');

        const url = `pps/completed-jobs/?${params.toString()}`;

        const { data } = await getRequest<CompletedJobsResponse>(url);

        // Ensure we never return more than 20 results on the frontend as well
        const limitedData = {
          ...data,
          jobs: data.jobs.slice(0, 20)
        };

        resolve(limitedData);
      } catch (error) {
        reject(error);
      }
    });
  },

  checkRedisDataForRoom: (roomId: string): Promise<RedisEditCheckResponse> => {
    return new Promise(async (resolve, reject) => {
      try {
        // Add timeout to prevent hanging
        const timeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(() => reject(new Error('Request timeout')), 10000); // 10 second timeout
        });

        const requestPromise = getRequest<RedisEditCheckResponse>(`pps/crdt/edit-check/${roomId}/`);

        const { data } = await Promise.race([requestPromise, timeoutPromise]);
        resolve(data);
      } catch (error) {
        reject(error);
      }
    });
  },

  getPpsBulkUpdatePreviewData: (formData: FormData): Promise<bulkUploadDataValidationInterface> => {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await formDataPostRequest<bulkUploadDataValidationInterface>(
          'pps/bulk-update-data-preview/',
          formData
        );
        resolve(data);
      } catch (error) {
        reject(error);
      }
    });
  },

  bulkUpdateJobs: (
    payload: BulkUploadDataValidationInterfaceInstanceRequest[]
  ): Promise<bulkUploadPpsResponseInterface> => {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await postRequest<bulkUploadPpsResponseInterface>(
          'pps/pps-bulk-upload-jobs/',
          payload
        );
        resolve(data);
      } catch (error) {
        reject(error);
      }
    });
  },

  saveEditedRoomJobs: (payload: EditJobsRequestSchema): Promise<CRDTProcessEditResponse> => {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await postRequest<CRDTProcessEditResponse>(
          'pps/crdt/process-edit/',
          payload
        );
        resolve(data);
      } catch (error) {
        reject(error);
      }
    });
  },

  addJobNote: (payload: AddJobNoteRequest): Promise<SingleJobNoteResponse> => {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await postRequest<SingleJobNoteResponse>('pps/notes/', payload);
        resolve(data);
      } catch (error) {
        reject(error);
      }
    });
  },

  updateJobNote: (
    noteId: number,
    payload: UpdateJobNoteRequest
  ): Promise<SingleJobNoteResponse> => {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await putRequest<SingleJobNoteResponse>(`pps/notes/${noteId}/`, payload);
        resolve(data);
      } catch (error) {
        reject(error);
      }
    });
  },

  getJobNotes: (jobId: string): Promise<JobNotesResponse> => {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await postRequest<JobNotesResponse>(`pps/notes/job/${jobId}/`, {});
        resolve(data);
      } catch (error) {
        reject(error);
      }
    });
  },

  getPossibleStatusTransitions: (
    department: string
  ): Promise<Record<string, StatusTransition[]>> => {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await getRequest<Record<string, StatusTransition[]>>(
          `pps/status-transitions/possible/?department_name=${department}`
        );
        resolve(data);
      } catch (error) {
        reject(error);
      }
    });
  },

  getSharedFilter: (): Promise<SharedFilterResponse> => {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await getRequest<SharedFilterResponse>('pps/shared-filter/');
        resolve(data);
      } catch (error) {
        reject(error);
      }
    });
  },

  updateSharedFilter: (payload: UpdateSharedFilterRequest): Promise<UpdateSharedFilterResponse> => {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await postRequest<UpdateSharedFilterResponse>(
          'pps/shared-filter/',
          payload
        );
        resolve(data);
      } catch (error) {
        reject(error);
      }
    });
  },

  flushAllDrafts: (): Promise<FlushAllDraftsResponse | FlushAllDraftsError> => {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await getRequest<FlushAllDraftsResponse | FlushAllDraftsError>(
          'pps/flush-all-drafts/'
        );
        resolve(data);
      } catch (error) {
        reject(error);
      }
    });
  },

  fetchAllRoomsFromRedis: (): Promise<FetchAllRoomsFromRedisResponse> => {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await getRequest<FetchAllRoomsFromRedisResponse>(
          'pps/crdt/fetch-all-rooms-from-redis/'
        );
        resolve(data);
      } catch (error) {
        reject(error);
      }
    });
  }
};
